// This file was auto-generated by Fern from our API Definition.
import { normalizeClientOptionsWithAuth } from "../../../../BaseClient.js";
import { mergeHeaders } from "../../../../core/headers.js";
import * as core from "../../../../core/index.js";
import * as environments from "../../../../environments.js";
import { handleNonStatusCodeError } from "../../../../errors/handleNonStatusCodeError.js";
import * as errors from "../../../../errors/index.js";
import * as AgentaApi from "../../../index.js";
/**
 * Inbound provider event triggers and their watchable event catalog.
 */
export class TriggersClient {
    _options;
    constructor(options) {
        this._options = normalizeClientOptionsWithAuth(options);
    }
    /**
     * Receive a Composio provider event; verify, demux, ack-fast, enqueue.
     *
     * Public (no Agenta auth) — mirrors the Stripe events receiver. Scope and
     * attribution are recovered downstream from the resolved subscription row.
     *
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @example
     *     await client.triggers.ingestComposioEvent()
     */
    ingestComposioEvent(requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__ingestComposioEvent(requestOptions));
    }
    async __ingestComposioEvent(requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "triggers/composio/events/"),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            throw new errors.AgentaApiError({
                statusCode: _response.error.statusCode,
                body: _response.error.body,
                rawResponse: _response.rawResponse,
            });
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/composio/events/");
    }
    /**
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @example
     *     await client.triggers.listTriggerProviders()
     */
    listTriggerProviders(requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__listTriggerProviders(requestOptions));
    }
    async __listTriggerProviders(requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "triggers/catalog/providers/"),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            throw new errors.AgentaApiError({
                statusCode: _response.error.statusCode,
                body: _response.error.body,
                rawResponse: _response.rawResponse,
            });
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/triggers/catalog/providers/");
    }
    /**
     * @param {AgentaApi.FetchTriggerProviderRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.fetchTriggerProvider({
     *         provider_key: "provider_key"
     *     })
     */
    fetchTriggerProvider(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchTriggerProvider(request, requestOptions));
    }
    async __fetchTriggerProvider(request, requestOptions) {
        const { provider_key: providerKey } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/catalog/providers/${core.url.encodePathParam(providerKey)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/triggers/catalog/providers/{provider_key}");
    }
    /**
     * @param {AgentaApi.ListTriggerIntegrationsRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.listTriggerIntegrations({
     *         provider_key: "provider_key"
     *     })
     */
    listTriggerIntegrations(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__listTriggerIntegrations(request, requestOptions));
    }
    async __listTriggerIntegrations(request, requestOptions) {
        const { provider_key: providerKey, search, sort_by: sortBy, limit, cursor } = request;
        const _queryParams = {
            search,
            sort_by: sortBy,
            limit,
            cursor,
        };
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/catalog/providers/${core.url.encodePathParam(providerKey)}/integrations/`),
            method: "GET",
            headers: _headers,
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/triggers/catalog/providers/{provider_key}/integrations/");
    }
    /**
     * @param {AgentaApi.FetchTriggerIntegrationRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.fetchTriggerIntegration({
     *         provider_key: "provider_key",
     *         integration_key: "integration_key"
     *     })
     */
    fetchTriggerIntegration(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchTriggerIntegration(request, requestOptions));
    }
    async __fetchTriggerIntegration(request, requestOptions) {
        const { provider_key: providerKey, integration_key: integrationKey } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/catalog/providers/${core.url.encodePathParam(providerKey)}/integrations/${core.url.encodePathParam(integrationKey)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/triggers/catalog/providers/{provider_key}/integrations/{integration_key}");
    }
    /**
     * @param {AgentaApi.ListTriggerEventsRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.listTriggerEvents({
     *         provider_key: "provider_key",
     *         integration_key: "integration_key"
     *     })
     */
    listTriggerEvents(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__listTriggerEvents(request, requestOptions));
    }
    async __listTriggerEvents(request, requestOptions) {
        const { provider_key: providerKey, integration_key: integrationKey, query, limit, cursor } = request;
        const _queryParams = {
            query,
            limit,
            cursor,
        };
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/catalog/providers/${core.url.encodePathParam(providerKey)}/integrations/${core.url.encodePathParam(integrationKey)}/events/`),
            method: "GET",
            headers: _headers,
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/triggers/catalog/providers/{provider_key}/integrations/{integration_key}/events/");
    }
    /**
     * @param {AgentaApi.FetchTriggerEventRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.fetchTriggerEvent({
     *         provider_key: "provider_key",
     *         integration_key: "integration_key",
     *         event_key: "event_key"
     *     })
     */
    fetchTriggerEvent(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchTriggerEvent(request, requestOptions));
    }
    async __fetchTriggerEvent(request, requestOptions) {
        const { provider_key: providerKey, integration_key: integrationKey, event_key: eventKey } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/catalog/providers/${core.url.encodePathParam(providerKey)}/integrations/${core.url.encodePathParam(integrationKey)}/events/${core.url.encodePathParam(eventKey)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/triggers/catalog/providers/{provider_key}/integrations/{integration_key}/events/{event_key}");
    }
    /**
     * @param {AgentaApi.TriggerDiscoveryQuery} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.discoverTriggers({
     *         use_cases: ["use_cases"]
     *     })
     */
    discoverTriggers(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__discoverTriggers(request, requestOptions));
    }
    async __discoverTriggers(request, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "triggers/discover"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/discover");
    }
    /**
     * @param {AgentaApi.QueryTriggerConnectionsRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.queryTriggerConnections()
     */
    queryTriggerConnections(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__queryTriggerConnections(request, requestOptions));
    }
    async __queryTriggerConnections(request = {}, requestOptions) {
        const { provider_key: providerKey, integration_key: integrationKey } = request;
        const _queryParams = {
            provider_key: providerKey,
            integration_key: integrationKey,
        };
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "triggers/connections/query"),
            method: "POST",
            headers: _headers,
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/connections/query");
    }
    /**
     * @param {AgentaApi.TriggerConnectionCreateRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.createTriggerConnection({
     *         connection: {
     *             provider_key: "composio",
     *             integration_key: "integration_key"
     *         }
     *     })
     */
    createTriggerConnection(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__createTriggerConnection(request, requestOptions));
    }
    async __createTriggerConnection(request, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "triggers/connections/"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/connections/");
    }
    /**
     * @param {AgentaApi.FetchTriggerConnectionRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.fetchTriggerConnection({
     *         connection_id: "connection_id"
     *     })
     */
    fetchTriggerConnection(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchTriggerConnection(request, requestOptions));
    }
    async __fetchTriggerConnection(request, requestOptions) {
        const { connection_id: connectionId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/connections/${core.url.encodePathParam(connectionId)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/triggers/connections/{connection_id}");
    }
    /**
     * @param {AgentaApi.DeleteTriggerConnectionRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.deleteTriggerConnection({
     *         connection_id: "connection_id"
     *     })
     */
    deleteTriggerConnection(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__deleteTriggerConnection(request, requestOptions));
    }
    async __deleteTriggerConnection(request, requestOptions) {
        const { connection_id: connectionId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/connections/${core.url.encodePathParam(connectionId)}`),
            method: "DELETE",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: undefined, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "DELETE", "/triggers/connections/{connection_id}");
    }
    /**
     * @param {AgentaApi.RefreshTriggerConnectionRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.refreshTriggerConnection({
     *         connection_id: "connection_id"
     *     })
     */
    refreshTriggerConnection(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__refreshTriggerConnection(request, requestOptions));
    }
    async __refreshTriggerConnection(request, requestOptions) {
        const { connection_id: connectionId, force } = request;
        const _queryParams = {
            force,
        };
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/connections/${core.url.encodePathParam(connectionId)}/refresh`),
            method: "POST",
            headers: _headers,
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/connections/{connection_id}/refresh");
    }
    /**
     * @param {AgentaApi.RevokeTriggerConnectionRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.revokeTriggerConnection({
     *         connection_id: "connection_id"
     *     })
     */
    revokeTriggerConnection(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__revokeTriggerConnection(request, requestOptions));
    }
    async __revokeTriggerConnection(request, requestOptions) {
        const { connection_id: connectionId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/connections/${core.url.encodePathParam(connectionId)}/revoke`),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/connections/{connection_id}/revoke");
    }
    /**
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @example
     *     await client.triggers.listTriggerSubscriptions()
     */
    listTriggerSubscriptions(requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__listTriggerSubscriptions(requestOptions));
    }
    async __listTriggerSubscriptions(requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "triggers/subscriptions/"),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            throw new errors.AgentaApiError({
                statusCode: _response.error.statusCode,
                body: _response.error.body,
                rawResponse: _response.rawResponse,
            });
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/triggers/subscriptions/");
    }
    /**
     * @param {AgentaApi.TriggerSubscriptionCreateRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.createTriggerSubscription({
     *         subscription: {
     *             connection_id: "connection_id",
     *             data: {
     *                 event_key: "event_key"
     *             }
     *         }
     *     })
     */
    createTriggerSubscription(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__createTriggerSubscription(request, requestOptions));
    }
    async __createTriggerSubscription(request, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "triggers/subscriptions/"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/subscriptions/");
    }
    /**
     * @param {AgentaApi.TriggerSubscriptionQueryRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.queryTriggerSubscriptions()
     */
    queryTriggerSubscriptions(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__queryTriggerSubscriptions(request, requestOptions));
    }
    async __queryTriggerSubscriptions(request = {}, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "triggers/subscriptions/query"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/subscriptions/query");
    }
    /**
     * @param {AgentaApi.TriggerSubscriptionCreateRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.testTriggerSubscription({
     *         subscription: {
     *             connection_id: "connection_id",
     *             data: {
     *                 event_key: "event_key"
     *             }
     *         }
     *     })
     */
    testTriggerSubscription(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__testTriggerSubscription(request, requestOptions));
    }
    async __testTriggerSubscription(request, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "triggers/subscriptions/test"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/subscriptions/test");
    }
    /**
     * @param {AgentaApi.RefreshTriggerSubscriptionRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.refreshTriggerSubscription({
     *         subscription_id: "subscription_id"
     *     })
     */
    refreshTriggerSubscription(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__refreshTriggerSubscription(request, requestOptions));
    }
    async __refreshTriggerSubscription(request, requestOptions) {
        const { subscription_id: subscriptionId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/subscriptions/${core.url.encodePathParam(subscriptionId)}/refresh`),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/subscriptions/{subscription_id}/refresh");
    }
    /**
     * @param {AgentaApi.RevokeTriggerSubscriptionRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.revokeTriggerSubscription({
     *         subscription_id: "subscription_id"
     *     })
     */
    revokeTriggerSubscription(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__revokeTriggerSubscription(request, requestOptions));
    }
    async __revokeTriggerSubscription(request, requestOptions) {
        const { subscription_id: subscriptionId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/subscriptions/${core.url.encodePathParam(subscriptionId)}/revoke`),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/subscriptions/{subscription_id}/revoke");
    }
    /**
     * @param {AgentaApi.StartTriggerSubscriptionRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.startTriggerSubscription({
     *         subscription_id: "subscription_id"
     *     })
     */
    startTriggerSubscription(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__startTriggerSubscription(request, requestOptions));
    }
    async __startTriggerSubscription(request, requestOptions) {
        const { subscription_id: subscriptionId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/subscriptions/${core.url.encodePathParam(subscriptionId)}/start`),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/subscriptions/{subscription_id}/start");
    }
    /**
     * @param {AgentaApi.StopTriggerSubscriptionRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.stopTriggerSubscription({
     *         subscription_id: "subscription_id"
     *     })
     */
    stopTriggerSubscription(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__stopTriggerSubscription(request, requestOptions));
    }
    async __stopTriggerSubscription(request, requestOptions) {
        const { subscription_id: subscriptionId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/subscriptions/${core.url.encodePathParam(subscriptionId)}/stop`),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/subscriptions/{subscription_id}/stop");
    }
    /**
     * @param {AgentaApi.FetchTriggerSubscriptionRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.fetchTriggerSubscription({
     *         subscription_id: "subscription_id"
     *     })
     */
    fetchTriggerSubscription(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchTriggerSubscription(request, requestOptions));
    }
    async __fetchTriggerSubscription(request, requestOptions) {
        const { subscription_id: subscriptionId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/subscriptions/${core.url.encodePathParam(subscriptionId)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/triggers/subscriptions/{subscription_id}");
    }
    /**
     * @param {AgentaApi.TriggerSubscriptionEditRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.editTriggerSubscription({
     *         subscription_id: "subscription_id",
     *         subscription: {
     *             connection_id: "connection_id",
     *             data: {
     *                 event_key: "event_key"
     *             }
     *         }
     *     })
     */
    editTriggerSubscription(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__editTriggerSubscription(request, requestOptions));
    }
    async __editTriggerSubscription(request, requestOptions) {
        const { subscription_id: subscriptionId, ..._body } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/subscriptions/${core.url.encodePathParam(subscriptionId)}`),
            method: "PUT",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: _body,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "PUT", "/triggers/subscriptions/{subscription_id}");
    }
    /**
     * @param {AgentaApi.DeleteTriggerSubscriptionRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.deleteTriggerSubscription({
     *         subscription_id: "subscription_id"
     *     })
     */
    deleteTriggerSubscription(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__deleteTriggerSubscription(request, requestOptions));
    }
    async __deleteTriggerSubscription(request, requestOptions) {
        const { subscription_id: subscriptionId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/subscriptions/${core.url.encodePathParam(subscriptionId)}`),
            method: "DELETE",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: undefined, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "DELETE", "/triggers/subscriptions/{subscription_id}");
    }
    /**
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @example
     *     await client.triggers.listTriggerSchedules()
     */
    listTriggerSchedules(requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__listTriggerSchedules(requestOptions));
    }
    async __listTriggerSchedules(requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "triggers/schedules/"),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            throw new errors.AgentaApiError({
                statusCode: _response.error.statusCode,
                body: _response.error.body,
                rawResponse: _response.rawResponse,
            });
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/triggers/schedules/");
    }
    /**
     * @param {AgentaApi.TriggerScheduleCreateRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.createTriggerSchedule({
     *         schedule: {
     *             data: {
     *                 event_key: "event_key",
     *                 schedule: "schedule"
     *             }
     *         }
     *     })
     */
    createTriggerSchedule(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__createTriggerSchedule(request, requestOptions));
    }
    async __createTriggerSchedule(request, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "triggers/schedules/"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/schedules/");
    }
    /**
     * @param {AgentaApi.TriggerScheduleQueryRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.queryTriggerSchedules()
     */
    queryTriggerSchedules(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__queryTriggerSchedules(request, requestOptions));
    }
    async __queryTriggerSchedules(request = {}, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "triggers/schedules/query"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/schedules/query");
    }
    /**
     * @param {AgentaApi.FetchTriggerScheduleRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.fetchTriggerSchedule({
     *         schedule_id: "schedule_id"
     *     })
     */
    fetchTriggerSchedule(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchTriggerSchedule(request, requestOptions));
    }
    async __fetchTriggerSchedule(request, requestOptions) {
        const { schedule_id: scheduleId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/schedules/${core.url.encodePathParam(scheduleId)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/triggers/schedules/{schedule_id}");
    }
    /**
     * @param {AgentaApi.TriggerScheduleEditRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.editTriggerSchedule({
     *         schedule_id: "schedule_id",
     *         schedule: {
     *             data: {
     *                 event_key: "event_key",
     *                 schedule: "schedule"
     *             }
     *         }
     *     })
     */
    editTriggerSchedule(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__editTriggerSchedule(request, requestOptions));
    }
    async __editTriggerSchedule(request, requestOptions) {
        const { schedule_id: scheduleId, ..._body } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/schedules/${core.url.encodePathParam(scheduleId)}`),
            method: "PUT",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: _body,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "PUT", "/triggers/schedules/{schedule_id}");
    }
    /**
     * @param {AgentaApi.DeleteTriggerScheduleRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.deleteTriggerSchedule({
     *         schedule_id: "schedule_id"
     *     })
     */
    deleteTriggerSchedule(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__deleteTriggerSchedule(request, requestOptions));
    }
    async __deleteTriggerSchedule(request, requestOptions) {
        const { schedule_id: scheduleId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/schedules/${core.url.encodePathParam(scheduleId)}`),
            method: "DELETE",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: undefined, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "DELETE", "/triggers/schedules/{schedule_id}");
    }
    /**
     * @param {AgentaApi.StartTriggerScheduleRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.startTriggerSchedule({
     *         schedule_id: "schedule_id"
     *     })
     */
    startTriggerSchedule(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__startTriggerSchedule(request, requestOptions));
    }
    async __startTriggerSchedule(request, requestOptions) {
        const { schedule_id: scheduleId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/schedules/${core.url.encodePathParam(scheduleId)}/start`),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/schedules/{schedule_id}/start");
    }
    /**
     * @param {AgentaApi.StopTriggerScheduleRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.stopTriggerSchedule({
     *         schedule_id: "schedule_id"
     *     })
     */
    stopTriggerSchedule(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__stopTriggerSchedule(request, requestOptions));
    }
    async __stopTriggerSchedule(request, requestOptions) {
        const { schedule_id: scheduleId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/schedules/${core.url.encodePathParam(scheduleId)}/stop`),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/schedules/{schedule_id}/stop");
    }
    /**
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @example
     *     await client.triggers.listTriggerDeliveries()
     */
    listTriggerDeliveries(requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__listTriggerDeliveries(requestOptions));
    }
    async __listTriggerDeliveries(requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "triggers/deliveries"),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            throw new errors.AgentaApiError({
                statusCode: _response.error.statusCode,
                body: _response.error.body,
                rawResponse: _response.rawResponse,
            });
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/triggers/deliveries");
    }
    /**
     * @param {AgentaApi.TriggerDeliveryQueryRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.queryTriggerDeliveries()
     */
    queryTriggerDeliveries(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__queryTriggerDeliveries(request, requestOptions));
    }
    async __queryTriggerDeliveries(request = {}, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "triggers/deliveries/query"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/triggers/deliveries/query");
    }
    /**
     * @param {AgentaApi.FetchTriggerDeliveryRequest} request
     * @param {TriggersClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.triggers.fetchTriggerDelivery({
     *         delivery_id: "delivery_id"
     *     })
     */
    fetchTriggerDelivery(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchTriggerDelivery(request, requestOptions));
    }
    async __fetchTriggerDelivery(request, requestOptions) {
        const { delivery_id: deliveryId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `triggers/deliveries/${core.url.encodePathParam(deliveryId)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/triggers/deliveries/{delivery_id}");
    }
}
